import { useEffect, useRef, useState } from 'react'
import { MdOutlineArrowCircleRight, MdArrowRightAlt } from 'react-icons/md'
import './FaqSection.css'
import { useFaqs } from '../../../hooks/useFaqs'
import { Link } from 'react-router-dom'

function FaqItem({ item, index, isOpen, onToggle }) {
  const bodyRef = useRef(null)
  const [maxHeight, setMaxHeight] = useState(isOpen ? 'none' : '0px')

  useEffect(() => {
    const el = bodyRef.current
    if (!el) return
    if (isOpen) {
      setMaxHeight(`${el.scrollHeight}px`)
    } else {
      setMaxHeight('0px')
    }
  }, [isOpen])

  return (
    <div className={`faq-item${isOpen ? ' open' : ''}`}>
      <button type="button" className="faq-q" onClick={onToggle}>
        <span className="faq-num">{String(index + 1).padStart(2, '0')}</span>
        <span className="faq-q-text">{item.q}</span>
        <span className="faq-icon">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 2V12M2 7H12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      <div className="faq-body" ref={bodyRef} style={{ maxHeight }}>
        <div className="faq-body-inner">{item.a}</div>
      </div>
    </div>
  )
}

export default function FaqSection() {
  const sectionRef = useRef(null)
  const [entered, setEntered] = useState(false)
  const [openIndex, setOpenIndex] = useState(0)
  const { faqs: faqList } = useFaqs()

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setEntered(true)
        observer.disconnect()
      },
      { threshold: 0.08 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <section id="faq" ref={sectionRef}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.03 }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="faq-dots-pattern" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="#B45309" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#faq-dots-pattern)" />
        </svg>
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '420px', height: '420px', opacity: 0.06 }}>
          <svg viewBox="0 0 420 420" xmlns="http://www.w3.org/2000/svg">
            <circle cx="210" cy="210" r="190" fill="none" stroke="#B45309" strokeWidth="1" />
            <circle cx="210" cy="210" r="148" fill="none" stroke="#D97706" strokeWidth="0.7" />
            <circle cx="210" cy="210" r="106" fill="none" stroke="#F59E0B" strokeWidth="0.5" strokeDasharray="4 6" />
          </svg>
        </div>
        <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '300px', height: '300px', opacity: 0.04 }}>
          <svg viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
            <circle cx="150" cy="150" r="130" fill="none" stroke="#B45309" strokeWidth="1" />
            <circle cx="150" cy="150" r="90" fill="none" stroke="#D97706" strokeWidth="0.6" />
          </svg>
        </div>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 55% 50% at 100% 50%,rgba(217,119,6,0.06) 0%,transparent 65%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(217,119,6,0.30),transparent)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(217,119,6,0.30),transparent)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div
          id="faq-grid"
          className="grid grid-cols-1 lg:grid-cols-[380px_1fr]"
          style={{ gap: '52px', alignItems: 'start' }}
        >
          <div className={`faq-al${entered ? ' faq-on' : ''}`} style={{ animationDelay: '.06s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span style={{ height: '1px', width: '28px', background: 'rgba(180,83,9,0.5)', display: 'block', flexShrink: 0 }} />
              <span style={{ fontFamily: 'var(--font-heading),serif', fontSize: '9.5px', fontWeight: 600, letterSpacing: '.32em', color: '#B45309', textTransform: 'uppercase' }}>
                Knowledge Base
              </span>
              <span style={{ height: '1px', width: '28px', background: 'rgba(180,83,9,0.5)', display: 'block', flexShrink: 0 }} />
            </div>

            <h2 className="faq-heading">
              Frequently
              <br />
              Asked <span>Questions</span>
            </h2>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span style={{ height: '1px', flex: 1, maxWidth: '48px', background: 'linear-gradient(90deg,rgba(217,119,6,0.4),transparent)', display: 'block' }} />
              <span style={{ fontSize: '18px', color: '#D97706', opacity: 0.65, lineHeight: 1 }}>ॐ</span>
              <span style={{ height: '1px', flex: 1, maxWidth: '48px', background: 'linear-gradient(90deg,transparent,rgba(217,119,6,0.4))', display: 'block' }} />
            </div>

            <p className="faq-desc">Everything you need to know — clearly answered. Browse the common questions below.</p>

            <div className={`faq-contact-card faq-au${entered ? ' faq-on' : ''}`} style={{ animationDelay: '.20s' }}>
              <span className="faq-contact-label">Still have a question?</span>
              <span className="faq-contact-sub">If it isn&apos;t covered here, our team is happy to help personally.</span>
              <Link
                to="/contact"
                className="faq-contact-btn"
              >
                <MdOutlineArrowCircleRight size={18} style={{ opacity: 0.7 }} />
                Contact Us
                <MdArrowRightAlt size={18} />
              </Link>
            </div>
          </div>

          <div className={`faq-ar${entered ? ' faq-on' : ''}`} style={{ animationDelay: '.14s' }}>
            <div className="faq-list" id="faq-list">
              {faqList.map((item, index) => (
                <FaqItem
                  item={item}
                  index={index}
                  key={item.q}
                  isOpen={openIndex === index}
                  onToggle={() => setOpenIndex((cur) => (cur === index ? -1 : index))}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
